/**
 * Deal-specific tool types and validation schemas
 */

import type { FieldChange } from './tool.types';

/**
 * Deal stage type — keep in sync with Hub `deal_stage` enum
 */
export type DealStage =
  | 'lead'
  | 'qualified'
  | 'discovery'
  | 'proposal'
  | 'negotiation'
  | 'contract_signed'
  | 'deal_won'
  | 'hand_off'
  | 'started'
  | 'in_progress'
  | 'completed'
  | 'invoiced'
  | 'paid'
  | 'sm_lost_no_savings'
  | 'sm_lost_savings_customer_rejected'
  | 'wtc_lost_solution_rejected'
  | 'wtc_lost_over_budget'
  | 'wtc_lost_no_budget';

/**
 * Deal status type
 */
export type DealStatus = 'active' | 'won' | 'lost';

export const DEAL_STAGE_VALUES: readonly DealStage[] = [
  'lead',
  'qualified',
  'discovery',
  'proposal',
  'negotiation',
  'contract_signed',
  'deal_won',
  'hand_off',
  'started',
  'in_progress',
  'completed',
  'invoiced',
  'paid',
  'sm_lost_no_savings',
  'sm_lost_savings_customer_rejected',
  'wtc_lost_solution_rejected',
  'wtc_lost_over_budget',
  'wtc_lost_no_budget',
];

const LOST_STAGES: readonly DealStage[] = [
  'sm_lost_no_savings',
  'sm_lost_savings_customer_rejected',
  'wtc_lost_solution_rejected',
  'wtc_lost_over_budget',
  'wtc_lost_no_budget',
];

/**
 * Deal entity
 */
export interface Deal {
  id: string;
  company_id: string;
  company_name?: string;
  title: string;
  description?: string;
  amount: number;
  currency: string;
  stage: DealStage;
  close_date?: string;
  close_probability: number;
  is_recurring: boolean;
  monthly_amount?: number;
  is_committed: boolean;
  tags?: string[];
  notes?: string;
  last_activity_at: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  won_at?: string;
  lost_at?: string;
}

/**
 * Deal creation input
 */
export interface CreateDealInput {
  company_id: string;
  title: string;
  description?: string;
  /** Optional — omit when the user stated no amount; never fabricate a placeholder. */
  amount?: number;
  currency?: string;
  stage?: DealStage;
  close_date?: string;
  close_probability?: number;
  is_recurring?: boolean;
  monthly_amount?: number;
  is_committed?: boolean;
  tags?: string[];
  notes?: string;
  /** Hub app_users ids (uuids) resolved via find_employee — not contact ids. */
  assigned_users?: string[];
}

/**
 * Deal update input
 */
export interface UpdateDealInput {
  title?: string;
  description?: string;
  amount?: number;
  currency?: string;
  stage?: DealStage;
  close_date?: string;
  close_probability?: number;
  is_recurring?: boolean;
  monthly_amount?: number;
  is_committed?: boolean;
  tags?: string[];
  notes?: string;
  /** Hub app_users ids (uuids) resolved via find_employee — not contact ids. */
  assigned_users?: string[];
  last_activity_at?: string;
  won_at?: string;
  lost_at?: string;
}

/**
 * Deal filter options
 */
export interface DealFilters {
  stage?: DealStage[];
  company_id?: string;
  probability_min?: number;
  probability_max?: number;
  close_date_from?: string;
  close_date_to?: string;
  is_recurring?: boolean;
  is_committed?: boolean;
  tags?: string[];
}

/**
 * Deal analytics metrics
 */
export interface DealAnalytics {
  totalPipelineValue: number;
  weightedPipelineValue: number;
  committedRevenue: number;
  recurringMRR: number;
  dealCountByStage: Record<DealStage, number>;
  dealValueByStage: Record<DealStage, number>;
  winRate: number;
  averageDealSize: number;
  averageSalesCycleLength: number;
  totalDeals: number;
  activeDealCount: number;
  wonDealCount: number;
  lostDealCount: number;
}

/**
 * Deal grouped by stage
 */
export interface DealsByStage {
  byStage: Record<DealStage, {
    count: number;
    totalValue: number;
    averageProbability: number;
    deals: Array<{
      id: string;
      title: string;
      company_name?: string;
      amount: number;
      close_probability: number;
      close_date?: string;
      viewInApp: string;
    }>;
  }>;
  summary: {
    totalDeals: number;
    totalPipelineValue: number;
    wonDeals: number;
    lostDeals: number;
  };
}

/**
 * Deal preview for confirmation
 */
export interface DealPreview {
  type: 'preview';
  entity: 'deal';
  id: string;
  changes: Record<string, FieldChange>;
  confirmationToken: string;
  preview: Deal | Partial<Deal>;
}

/**
 * Deal tool parameters union
 */
export type DealToolParams = 
  | { toolId: 'find_deal'; params: { id?: string; company_id?: string } }
  | { toolId: 'list_deals'; params: DealFilters & { limit?: number; orderBy?: string } }
  | { toolId: 'search_deals'; params: { query: string; limit?: number } }
  | { toolId: 'get_deal_analytics'; params: { company_id?: string } }
  | { toolId: 'get_deals_by_stage'; params: { company_id?: string } }
  | { toolId: 'create_deal'; params: CreateDealInput }
  | { toolId: 'update_deal'; params: { id: string; updates: UpdateDealInput } }
  | { toolId: 'mark_deal_won'; params: { id: string; notes?: string } }
  | { toolId: 'mark_deal_lost'; params: { id: string; reason?: string; notes?: string } };

/**
 * Validation helper for deal stage
 */
export function isValidDealStage(stage: unknown): stage is DealStage {
  return typeof stage === 'string' && (DEAL_STAGE_VALUES as readonly string[]).includes(stage);
}

export function isLostDealStage(stage: DealStage): boolean {
  return (LOST_STAGES as readonly string[]).includes(stage);
}

/**
 * Validation helper for deal status
 */
export function isValidDealStatus(status: unknown): status is DealStatus {
  return typeof status === 'string' && ['active', 'won', 'lost'].includes(status);
}

/**
 * Validation helper for deal
 */
export function isValidDeal(obj: unknown): obj is Deal {
  if (typeof obj !== 'object' || obj === null) return false;
  const record = obj as Record<string, unknown>;
  return (
    typeof record.id === 'string' &&
    typeof record.company_id === 'string' &&
    typeof record.title === 'string' &&
    typeof record.amount === 'number' &&
    typeof record.currency === 'string' &&
    isValidDealStage(record.stage) &&
    typeof record.close_probability === 'number' &&
    typeof record.is_recurring === 'boolean' &&
    typeof record.is_committed === 'boolean' &&
    typeof record.created_at === 'string' &&
    typeof record.updated_at === 'string'
  );
}
